// App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import Navbar from "./components/Navbar";
import RouteSeo from "./components/RouteSeo";
import Hero from "./components/Hero";
import MovingText from "./components/MovingText";
import CustomCursor from "./components/CustomCursor";
import Expertise from "./components/Expertise";
import Journey from "./components/Journey";
import CaseStudies from "./components/CaseStudies";
import PublicWork from "./components/PublicWork";
import Contact from "./components/Contact";

import GoldenOpp from "./case-studies/pages/goldenopp";

import "./styles/global.css";
import JFS from "./case-studies/pages/jfs";
import TaxGenie from "./case-studies/pages/taxgenie";
import Deloitte250k from "./case-studies/pages/deloitte250k";
import Aitech from "./case-studies/pages/aitech";
import AgentForge from "./case-studies/pages/agentforge";

// page transition variants
// page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    x: -60,  // bottom-left direction
    y: 60,
  },
  in: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.33, 1, 0.68, 1], // smooth, no bounce
    },
  },
  out: {
    opacity: 0,
    x: 60,   // exit towards top-right
    y: -60,
    transition: {
      duration: 0.35,
      ease: [0.33, 1, 0.68, 1],
    },
  },
};


// scroll to top on every route change
function ScrollToTop() {
  const location = useLocation();
  const { pathname, state } = location;

  useEffect(() => {
    // always go top on route change
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    if (pathname !== "/" || !state?.scrollTo) return;

    // Home mounts after the page exit animation, so the target
    // may not exist on the first frame. Once it does, wait out
    // the enter animation before measuring its position.
    let frames = 0;
    let rafId = 0;
    let timeoutId = 0;

    const tryScroll = () => {
      const el = document.getElementById(state.scrollTo);
      if (el) {
        const delay = frames === 0 ? 0 : 480;
        timeoutId = window.setTimeout(() => {
          document
            .getElementById(state.scrollTo)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, delay);
        return;
      }
      frames += 1;
      if (frames < 90) {
        rafId = requestAnimationFrame(tryScroll);
      }
    };

    rafId = requestAnimationFrame(tryScroll);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [pathname, state]);

  return null;
}


// this component handles the animated route switching
function AnimatedRoutes({
  setShowLoupe,
  setShowLoupeSecret,
  setShowViewCursor,
}) {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
        className="page-wrapper"
      >
        <Routes location={location}>
          {/* Home page */}
          <Route
            path="/"
            element={
              <>
                <Hero />
                <MovingText />
                <Expertise
                  onLoupeActiveChange={setShowLoupe}
                  onLoupeSecretChange={setShowLoupeSecret}
                />
                <Journey />
                <CaseStudies onViewCursorChange={setShowViewCursor} />
                <PublicWork />
              </>
            }
          />

          {/* Golden Opportunity case study */}
          <Route
            path="/case-studies/golden-opportunity"
            element={<GoldenOpp />}
          />
          <Route
            path="/case-studies/johns-football-shirts"
            element={<JFS/>}
          />
          <Route
            path="/case-studies/deloitte-tax-genie"
            element={<TaxGenie/>}
          />
          <Route
            path="/case-studies/deloitte-250k-deal"
            element={<Deloitte250k />}
          />
          <Route
            path="/case-studies/the-company-that-forced-me-to-outgrow-myself"
            element={<Aitech />}
          />
          <Route
            path="/case-studies/the-platform-that-taught-me-how-to-think-bigger"
            element={<AgentForge />}
          />
          {/* other case pages later... */}
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [showLoupe, setShowLoupe] = useState(false);
  const [showLoupeSecret, setShowLoupeSecret] = useState(false);
  const [showViewCursor, setShowViewCursor] = useState(false);

  return (
    <Router>
      <RouteSeo />
      {/* this listens to route changes and always scrolls to top */}
      <ScrollToTop />
      <div className="app-container">
        <CustomCursor
          showLoupe={showLoupe}
          showSecret={showLoupeSecret}
          showView={showViewCursor}
        />

        <Navbar />

        <AnimatedRoutes
          setShowLoupe={setShowLoupe}
          setShowLoupeSecret={setShowLoupeSecret}
          setShowViewCursor={setShowViewCursor}
        />
      </div>
      <Contact />
    </Router>
  );
}

export default App;
